import { useEffect, useRef } from 'react';
import { debounce } from 'lodash';

import { subscribeToFrames } from 'src/services/websocket/live-updates-signal';
import type { DataFrame } from 'src/services/websocket/ws-protocol';

const FRAME_REFETCH_DEBOUNCE_MS = 1_000;

type UseLiveRefetchOptions = {
    isLive: boolean;
    enabled: boolean;
    refetch: () => unknown;
    shouldRefetchFrame: (frame: DataFrame) => boolean;
};

export function useLiveRefetch({
    isLive,
    enabled,
    refetch,
    shouldRefetchFrame,
}: UseLiveRefetchOptions): void {
    const previousIsLiveRef = useRef(isLive);
    const refetchRef = useRef(refetch);
    const shouldRefetchFrameRef = useRef(shouldRefetchFrame);
    refetchRef.current = refetch;
    shouldRefetchFrameRef.current = shouldRefetchFrame;

    useEffect(() => {
        const wasLive = previousIsLiveRef.current;
        previousIsLiveRef.current = isLive;
        if (enabled && isLive && !wasLive) {
            void refetchRef.current();
        }
    }, [enabled, isLive]);

    useEffect(() => {
        if (!enabled || !isLive) {
            return;
        }

        const debouncedRefetch = debounce(
            () => void refetchRef.current(),
            FRAME_REFETCH_DEBOUNCE_MS,
            { leading: true, trailing: true },
        );
        const unsubscribe = subscribeToFrames((frame) => {
            if (shouldRefetchFrameRef.current(frame)) {
                debouncedRefetch();
            }
        });

        return () => {
            unsubscribe();
            debouncedRefetch.cancel();
        };
    }, [enabled, isLive]);
}
